import { ArrowUpRight } from "lucide-react"
import { type ComponentType, memo, type SVGProps } from "react"

import { BrandLogo } from "@/shared/components/BrandLogo"
import { LanguageSwitcher } from "@/shared/components/ui/language-switcher"
import { buildFooter } from "@/shared/i18n/legacyShape"
import { useLocaleCopy } from "@/shared/i18n/useLocaleCopy"
import { cn } from "@/shared/utils"

type NavLink = {
  label: string
  href: string
  external?: boolean
}

function LinkedInIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.852 3.37-1.852 3.601 0 4.267 2.37 4.267 5.455v6.288zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

function XIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

function GithubIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  )
}

type SocialIcon = ComponentType<SVGProps<SVGSVGElement>>

const SOCIAL_ICONS: Record<string, SocialIcon> = {
  LinkedIn: LinkedInIcon,
  X: XIcon,
  GitHub: GithubIcon,
}

function FooterColumn({
  label,
  links,
}: {
  label: string
  links: ReadonlyArray<NavLink>
}) {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-[12px] font-semibold uppercase tracking-[0.08em] text-white/60">
        {label}
      </h3>
      <ul className="flex flex-col gap-2.5">
        {links.map((link) => (
          <li key={link.label}>
            <a
              href={link.href}
              target={link.external ? "_blank" : undefined}
              rel={link.external ? "noopener noreferrer" : undefined}
              className="group inline-flex items-center gap-1 text-[14px] text-white/75 transition-colors duration-200 hover:text-white"
            >
              {link.label}
              {link.external && (
                <ArrowUpRight
                  className="h-3 w-3 opacity-50 transition-opacity duration-200 group-hover:opacity-100"
                  aria-hidden="true"
                />
              )}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SocialLinks() {
  const copy = useLocaleCopy()
  const FOOTER_NAV = buildFooter(copy)
  return (
    <ul className="mt-2 flex items-center gap-2" aria-label="Redes sociais">
      {FOOTER_NAV.socials.map((social) => {
        const Icon = SOCIAL_ICONS[social.label]
        if (!Icon) {return null}
        return (
          <li key={social.label}>
            <a
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`MesaOTC no ${social.label}`}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5",
                "text-white/65 transition-colors duration-200 hover:border-white/20 hover:bg-white/10 hover:text-white",
              )}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
            </a>
          </li>
        )
      })}
    </ul>
  )
}

export const Footer = memo(function Footer() {
  const copy = useLocaleCopy()
  const FOOTER_NAV = buildFooter(copy)
  return (
    <footer
      id="rodape"
      aria-label="Rodapé MesaOTC"
      className="relative w-full bg-[#0A0E1A] pt-20 pb-10 md:pt-24 md:pb-12"
    >
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-12 md:gap-8">
          <div className="col-span-2 flex flex-col gap-5 md:col-span-4">
            <a
              href="#inicio"
              aria-label="MesaOTC — início"
              className="inline-flex w-fit text-white transition-opacity duration-200 hover:opacity-85"
            >
              <BrandLogo size="md" />
            </a>
            <p className="max-w-xs text-[14px] leading-[1.6] text-white/65">
              {FOOTER_NAV.brand.tagline}
            </p>

            <a
              href={FOOTER_NAV.meta.statusHref}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "group mt-2 inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5",
                "text-[11.5px] font-medium transition-colors duration-200 hover:border-white/20 hover:bg-white/10",
              )}
            >
              <span className="relative flex h-1.5 w-1.5 items-center justify-center">
                <span className="absolute inset-0 animate-ping rounded-full bg-mesa-accent opacity-60" />
                <span className="relative h-1.5 w-1.5 rounded-full bg-mesa-accent" />
              </span>
              <span className="text-white/85">{FOOTER_NAV.meta.statusLabel}</span>
              <ArrowUpRight
                className="h-3 w-3 text-white/50 transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white/80"
                aria-hidden="true"
              />
            </a>

            <SocialLinks />
          </div>

          {FOOTER_NAV.columns.map((column) => (
            <div key={column.label} className="md:col-span-2">
              <FooterColumn label={column.label} links={column.links} />
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-start gap-4 border-t border-white/10 pt-8 md:flex-row md:items-center md:justify-between">
          <p className="text-[12.5px] text-white/60">
            © {FOOTER_NAV.meta.year} {FOOTER_NAV.brand.name} · {FOOTER_NAV.meta.legalName} · {FOOTER_NAV.meta.address}
          </p>
          <div className="flex items-center gap-4">
            <LanguageSwitcher variant="dark" align="right" />
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-white/55">
              {FOOTER_NAV.meta.region}
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
})
